<template>
  <div class="checklist-create-update-list">
    <b-row>
      <b-col xs="12" sm="12" md="8" lg="8" xl="8">
        <InputText
            placeholder="Digite o checklist e enter"
            field="name"
            :borderRadius="50"
            :maxLength="100"
            :enter="addChecklist"
            v-model="checklist.name"
        />
      </b-col>
      <b-col xs="12" sm="12" md="4" lg="4" xl="4">
        <div class="checklist-create-update-list-action">
          <Button
              _key="addChecklist"
              type="primary"
              classIcon="fa-sharp fa-solid fa-plus"
              size="small"
              :loading="isLoading('addChecklist')"
              :disabled="!canAddChecklist"
              :clicked="addChecklist"
          />
        </div>
      </b-col>
    </b-row>

    <Loading v-if="loading" type="line"/>

    <ScrollBar :minHeight="0" :maxHeight="300">
      <div v-if="data.length === 0" class="checklist-create-update-list-empty">
        <i class="fa-regular fa-square-check"></i>
        <span>Nenhum checklist adicionado!</span>
      </div>

      <draggable
          v-else
          v-model="data"
          tag="div"
          class="checklist-create-update-list-items"
      >
        <div
            v-for="item in data"
            :key="item.id"
            class="checklist-create-update-list-item"
            :class="{ 'checklist-create-update-list-item-done': item.done }"
        >
          <div class="checklist-create-update-list-item-main">
            <div
                v-b-tooltip.hover
                title="Arraste para reordenar"
                class="checklist-create-update-list-item-drag"
            >
              <i class="fa-solid fa-grip-lines"></i>
            </div>

            <CheckboxSimple v-model="item.done"/>

            <div class="checklist-create-update-list-item-text">
              <InputTextEdit v-if="!item.done" v-model="item.name"/>
              <span v-else class="checklist-create-update-list-item-text-done">
                {{ item.name }}
              </span>
            </div>

            <div
                v-if="!item.done"
                class="checklist-create-update-list-item-remove"
                @click="remove(item)"
            >
              <Loading v-if="isLoading(item.id)" type="bubbles" :center="false" :width="20" :height="20" />
              <i v-else class="fa-solid fa-trash"></i>
            </div>
          </div>
        </div>
      </draggable>
    </ScrollBar>
  </div>
</template>

<script>
import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";
import ScrollBar from "@/ui/layout/ScrollBar.vue";
import Loading from "@/ui/shared/Loading.vue";
import CheckboxSimple from "@/ui/forms/CheckboxSimple";
import InputTextEdit from "@/ui/forms/InputTextEdit";
import draggable from "vuedraggable";

import Checklist from "../models/Checklist";
import ChecklistService from "../services/checklist.service";

import {mapGetters, mapMutations, mapState} from "vuex";

export default {
  name: "ChecklistList",
  props: {
    genericId: {
      type: String,
      required: true,
    },
  },
  components: {
    Button,
    InputText,
    ScrollBar,
    Loading,
    draggable,
    CheckboxSimple,
    InputTextEdit,
  },
  data() {
    return {
      data: [],
      checklist: new Checklist(),
      loading: true,
    };
  },
  mounted() {
    this.getAll();
  },
  computed: {
    ...mapState("user", ["userLogged"]),
    ...mapGetters("generic", ["isLoading"]),
    canAddChecklist() {
      return !!this.checklist.name.trim();
    },
  },
  methods: {
    ...mapMutations("generic", ["addLoading", "removeLoading"]),
    async getAll() {
      this.loading = true;
      const response = await ChecklistService.getAll(this.genericId);
      this.data = response.data.content.data;
      this.loading = false;
      this.emitCountChange();
    },
    async addChecklist() {
      if (this.canAddChecklist) {
        this.addLoading("addChecklist");
        try {
          this.checklist.genericId = this.genericId;
          this.checklist.userId = this.userLogged.user.id;
          this.checklist.name = this.checklist.name.trim();
          await ChecklistService.create(this.checklist);
          this.checklist = new Checklist();
          await this.getAll();
        } finally {
          this.removeLoading(["addChecklist"]);
        }
      }
    },
    async updateAll() {
      if (this.loading) return;
      await ChecklistService.updateAll(this.data);
    },
    async remove(item) {
      if (this.isLoading(item.id)) return;
      this.addLoading(item.id);
      try {
        await ChecklistService.remove(item.id);
        await this.getAll();
      } finally {
        this.removeLoading([item.id]);
      }
    },
    emitCountChange() {
      const done = this.data.filter((item) => item.done).length;
      this.$emit("count-change", { total: this.data.length, done });
    },
  },
  watch: {
    data: {
      handler() {
        this.updateAll();
        this.emitCountChange();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.checklist-create-update-list {
  margin-top: 10px;
  overflow-x: hidden;
}

.checklist-create-update-list-action {
  margin-top: 38px;
}

.checklist-create-update-list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--text-muted);
  font-size: 14px;
}

.checklist-create-update-list-empty i {
  font-size: 28px;
}

.checklist-create-update-list-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checklist-create-update-list-item {
  border: 1px solid var(--border-subtle);
  border-radius: 12px;
  background: var(--bg-base);
  padding: 8px 12px;
  transition: background 0.15s;
}

.checklist-create-update-list-item:hover {
  background: var(--bg-hover);
}

.checklist-create-update-list-item-done {
  background: var(--bg-elevated);
}

.checklist-create-update-list-item-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.checklist-create-update-list-item-drag {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: move;
  font-size: 14px;
}

.checklist-create-update-list-item-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.checklist-create-update-list-item-text-done {
  color: var(--text-secondary);
  text-decoration: line-through;
  overflow-wrap: break-word;
  word-break: break-word;
}

.checklist-create-update-list-item-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-danger);
  cursor: pointer;
  transition: color 0.2s;
}

.checklist-create-update-list-item-remove:hover {
  color: var(--color-brand-dark);
}
</style>
